import React, { useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useAdminOperatingCosts } from "../../api/hooks/admin";
import {
  OperatingCostEntry,
  OperatingCostsResponse,
  OperatingCostVendor,
} from "../../api/admin/types";
import AdminController from "../../controllers/AdminController";
import { formatInt } from "../MetricsPage/shared/formatters";
import { ACCENT, card, formatUsd, panel, select } from "./styles";

// Vendors we pay that have no billing API we can read; offered as one-click starts for a manual entry.
const UNTRACKED_SUGGESTIONS: { vendor: string; category: string }[] = [
  { vendor: "Pinecone", category: "Vector database" },
  { vendor: "Grafana Cloud", category: "Observability" },
  { vendor: "Stytch", category: "Authentication" },
  { vendor: "Intercom", category: "Customer support" },
  { vendor: "Slack", category: "Collaboration" },
  { vendor: "Google Workspace", category: "Collaboration" },
  { vendor: "Google Gemini API", category: "LLM APIs" },
  { vendor: "Claude Team / Max seats", category: "Staff AI tools" },
  { vendor: "Cohere", category: "LLM APIs" },
  { vendor: "DeepSeek", category: "LLM APIs" },
  { vendor: "Azure Document Intelligence", category: "Document processing" },
  { vendor: "LlamaCloud (LlamaParse)", category: "Document processing" },
  { vendor: "Anvil", category: "Document processing" },
  { vendor: "Twilio", category: "Messaging" },
  { vendor: "Tavily", category: "Search API" },
  { vendor: "PostHog", category: "Product analytics" },
  { vendor: "LangSmith", category: "Observability" },
  { vendor: "Linear", category: "Developer tools" },
  { vendor: "1Password", category: "Security" },
];

const VENDOR_COLORS = [
  "#4f46e5",
  "#0891b2",
  "#d97706",
  "#059669",
  "#dc2626",
  "#7c3aed",
  "#db2777",
  "#65a30d",
  "#0284c7",
  "#ea580c",
  "#475569",
  "#ca8a04",
];

const th: React.CSSProperties = {
  padding: 8,
  textAlign: "left",
  color: "#6b6259",
  fontWeight: 500,
};
const td: React.CSSProperties = { padding: 8, borderTop: "1px solid #eee", verticalAlign: "top" };
const num: React.CSSProperties = { ...td, textAlign: "right", whiteSpace: "nowrap" };
const button: React.CSSProperties = { ...select, background: "#fff", cursor: "pointer" };

function monthLabel(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

function sum(values: number[]): number {
  return values.reduce((a, b) => a + b, 0);
}

const SourceBadge: React.FC<{ vendor: OperatingCostVendor }> = ({ vendor }) => {
  const [label, bg, fg] =
    vendor.status === "needs_access"
      ? ["Needs access", "#fef3c7", "#92400e"]
      : vendor.status === "error"
        ? ["Error", "#fee2e2", "#7f1d1d"]
        : vendor.source === "manual"
          ? ["Entered by hand", "#f1f5f9", "#334155"]
          : ["Live billing API", "#dcfce7", "#14532d"];
  return (
    <span style={{ background: bg, color: fg, borderRadius: 4, padding: "1px 6px", fontSize: 12 }}>
      {label}
    </span>
  );
};

const Kpi: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div style={{ ...card, flex: "1 1 200px" }}>
    <div style={{ fontSize: 13, color: "#6b6259" }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 700, marginTop: 6 }}>{value}</div>
    {hint && <div style={{ fontSize: 13, color: "#6b6259", marginTop: 4 }}>{hint}</div>}
  </div>
);

const emptyEntry = (): OperatingCostEntry => ({
  vendor: "",
  category: "",
  amount_usd: 0,
  cadence: "monthly",
  start_month: currentMonth(),
  end_month: "",
  notes: "",
  source_url: "",
});

const EntryForm: React.FC<{
  initial: OperatingCostEntry;
  onSaved: () => void;
  onCancel: () => void;
}> = ({ initial, onSaved, onCancel }) => {
  const [entry, setEntry] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const set = <K extends keyof OperatingCostEntry>(k: K, v: OperatingCostEntry[K]) =>
    setEntry((e) => ({ ...e, [k]: v }));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await AdminController.saveOperatingCostEntry(entry);
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const field = (label: string, input: React.ReactNode) => (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      {label}
      {input}
    </label>
  );

  return (
    <form onSubmit={save} style={{ ...card, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {field(
          "Vendor",
          <input
            required
            style={select}
            value={entry.vendor}
            onChange={(e) => set("vendor", e.target.value)}
          />
        )}
        {field(
          "Category",
          <input
            style={select}
            value={entry.category}
            onChange={(e) => set("category", e.target.value)}
          />
        )}
        {field(
          "Amount (USD)",
          <input
            required
            type="number"
            min={0}
            step="0.01"
            style={{ ...select, width: 120 }}
            value={entry.amount_usd}
            onChange={(e) => set("amount_usd", Number(e.target.value))}
          />
        )}
        {field(
          "Billed",
          <select
            style={select}
            value={entry.cadence}
            onChange={(e) => set("cadence", e.target.value as OperatingCostEntry["cadence"])}
          >
            <option value="monthly">Monthly</option>
            <option value="annual">Annually (shown as 1/12 a month)</option>
          </select>
        )}
        {field(
          "From",
          <input
            required
            type="month"
            style={select}
            value={entry.start_month}
            onChange={(e) => set("start_month", e.target.value)}
          />
        )}
        {field(
          "Until (blank = ongoing)",
          <input
            type="month"
            style={select}
            value={entry.end_month}
            min={entry.start_month}
            onChange={(e) => set("end_month", e.target.value)}
          />
        )}
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {field(
          "Notes (plan, seats, what it covers)",
          <input
            style={{ ...select, maxWidth: 420, width: 420 }}
            value={entry.notes}
            onChange={(e) => set("notes", e.target.value)}
          />
        )}
        {field(
          "Invoice or billing page link",
          <input
            type="url"
            style={{ ...select, maxWidth: 420, width: 420 }}
            value={entry.source_url}
            onChange={(e) => set("source_url", e.target.value)}
          />
        )}
      </div>
      {error && (
        <div role="alert" style={{ color: "#7f1d1d", fontSize: 13 }}>
          {error}
        </div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="submit"
          disabled={saving}
          style={{ ...button, background: ACCENT, color: "#fff", borderColor: ACCENT }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={onCancel} style={button}>
          Cancel
        </button>
      </div>
    </form>
  );
};

const ManualEntries: React.FC<{ data: OperatingCostsResponse; onChanged: () => void }> = ({
  data,
  onChanged,
}) => {
  const [editing, setEditing] = useState<OperatingCostEntry | null>(null);
  const tracked = new Set(data.vendors.map((v) => v.vendor.toLowerCase()));
  const suggestions = UNTRACKED_SUGGESTIONS.filter((s) => !tracked.has(s.vendor.toLowerCase()));

  const remove = async (entry: OperatingCostEntry) => {
    if (!entry.id || !window.confirm(`Delete the ${entry.vendor} cost?`)) return;
    await AdminController.deleteOperatingCostEntry(entry.id);
    onChanged();
  };

  return (
    <div style={{ ...panel, display: "flex", flexDirection: "column", gap: 12 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 16 }}>Costs entered by hand</h3>
          <p style={{ margin: "4px 0 0", color: "#6b6259", fontSize: 13 }}>
            For vendors with no billing API we can read. Copy the amount from the invoice.
          </p>
        </div>
        {!editing && (
          <button style={button} onClick={() => setEditing(emptyEntry())}>
            Add a cost
          </button>
        )}
      </div>

      {editing && (
        <EntryForm
          key={editing.id ?? editing.vendor}
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      )}

      {data.manual_entries.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr>
                <th style={th}>Vendor</th>
                <th style={th}>Category</th>
                <th style={{ ...th, textAlign: "right" }}>Amount</th>
                <th style={th}>Active</th>
                <th style={th}>Notes</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {data.manual_entries.map((e) => (
                <tr key={e.id}>
                  <td style={td}>
                    {e.source_url ? (
                      <a href={e.source_url} target="_blank" rel="noreferrer">
                        {e.vendor}
                      </a>
                    ) : (
                      e.vendor
                    )}
                  </td>
                  <td style={td}>{e.category}</td>
                  <td style={num}>
                    {formatUsd(e.amount_usd)} / {e.cadence === "annual" ? "year" : "month"}
                  </td>
                  <td style={td}>
                    {monthLabel(e.start_month)} – {e.end_month ? monthLabel(e.end_month) : "now"}
                  </td>
                  <td style={{ ...td, color: "#6b6259" }}>{e.notes}</td>
                  <td style={{ ...td, whiteSpace: "nowrap", textAlign: "right" }}>
                    <button style={{ ...button, padding: "2px 8px" }} onClick={() => setEditing(e)}>
                      Edit
                    </button>{" "}
                    <button style={{ ...button, padding: "2px 8px" }} onClick={() => remove(e)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {suggestions.length > 0 && (
        <div style={{ fontSize: 13, color: "#6b6259" }}>
          Not tracked yet:{" "}
          {suggestions.map((s) => (
            <button
              key={s.vendor}
              onClick={() =>
                setEditing({ ...emptyEntry(), vendor: s.vendor, category: s.category })
              }
              style={{ ...button, padding: "2px 8px", margin: "2px 4px 2px 0", fontSize: 12 }}
            >
              + {s.vendor}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const OperatingCostsTab: React.FC = () => {
  const [months, setMonths] = useState(6);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [detailMonth, setDetailMonth] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { data, error, isLoading, isFetching } = useAdminOperatingCosts(months, refreshNonce);

  const reload = () => queryClient.invalidateQueries("admin-operating-costs");

  const derived = useMemo(() => {
    if (!data) return null;
    const totalFor = (m: string) => sum(data.vendors.map((v) => v.monthly[m] ?? 0));
    const fullMonths = data.months.slice(0, -1);
    const withSpend = data.vendors.filter((v) => Object.values(v.monthly).some(Boolean));
    const chart = data.months.map((m) => ({
      month: monthLabel(m),
      ...Object.fromEntries(withSpend.map((v) => [v.vendor, v.monthly[m] ?? 0])),
    }));
    const vendors = [...data.vendors].sort(
      (a, b) => sum(Object.values(b.monthly)) - sum(Object.values(a.monthly))
    );
    return { totalFor, fullMonths, withSpend, chart, vendors };
  }, [data]);

  if (error) {
    return (
      <div role="alert" style={{ ...panel, color: "#7f1d1d" }}>
        {(error as Error).message}
      </div>
    );
  }
  if (isLoading || !data || !derived) {
    return (
      <div style={{ ...panel, color: "#6b6259" }}>
        Reading billing data from each vendor… the first load can take up to a minute.
      </div>
    );
  }

  const { totalFor, fullMonths, withSpend, chart, vendors } = derived;
  const lastFull = fullMonths[fullMonths.length - 1];
  const thisMonth = data.months[data.months.length - 1];
  const detail =
    detailMonth && data.months.includes(detailMonth) ? detailMonth : (lastFull ?? thisMonth);
  const blocked = data.vendors.filter((v) => v.status !== "ok");
  const people = data.claude_code.by_month[detail] ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <p style={{ margin: 0, color: "#6b6259", fontSize: 14 }}>
        What Coverflow is actually billed for software, read from each vendor's billing API where we
        have access, plus costs entered by hand from invoices.
      </p>

      <div style={{ ...card, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
          Period
          <select style={select} value={months} onChange={(e) => setMonths(Number(e.target.value))}>
            <option value={3}>Last 3 months</option>
            <option value={6}>Last 6 months</option>
            <option value={12}>Last 12 months</option>
          </select>
        </label>
        <button style={button} disabled={isFetching} onClick={() => setRefreshNonce((n) => n + 1)}>
          {isFetching ? "Reading vendors…" : "Refresh from vendors"}
        </button>
        <span style={{ fontSize: 13, color: "#6b6259" }}>
          Vendor data read {new Date(data.fetched_at).toLocaleString()}; refreshed at most every 6
          hours unless you refresh.
        </span>
      </div>

      {blocked.length > 0 && (
        <div style={{ ...card, background: "#fffbeb", borderColor: "#fde68a" }} role="status">
          <strong>
            Missing billing access for {blocked.length} vendor{blocked.length > 1 ? "s" : ""}.
          </strong>{" "}
          Their costs aren't in the totals yet.
          <ul style={{ margin: "8px 0 0", paddingLeft: 20, fontSize: 14 }}>
            {blocked.map((v) => (
              <li key={v.vendor}>
                <strong>{v.vendor}</strong>:{" "}
                {v.status === "needs_access" ? `add ${v.needs.join(", ")}` : v.error}
                {v.error && v.status === "needs_access" ? ` (${v.error})` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <Kpi
          label={`Last full month (${monthLabel(lastFull ?? thisMonth)})`}
          value={formatUsd(totalFor(lastFull ?? thisMonth))}
        />
        <Kpi
          label="This month so far"
          value={formatUsd(totalFor(thisMonth))}
          hint={monthLabel(thisMonth)}
        />
        <Kpi
          label="Average per full month"
          value={formatUsd(
            fullMonths.length ? sum(fullMonths.map(totalFor)) / fullMonths.length : 0
          )}
          hint={`${fullMonths.length} month${fullMonths.length === 1 ? "" : "s"}`}
        />
        <Kpi
          label="Vendors with costs"
          value={`${withSpend.length}`}
          hint={`${data.vendors.filter((v) => v.status === "ok").length} of ${data.vendors.length} readable`}
        />
      </div>

      <div style={panel}>
        <h3 style={{ margin: "0 0 12px", fontSize: 16 }}>Spend per month by vendor</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chart} margin={{ left: 8, right: 16 }}>
            <CartesianGrid stroke="#eee" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v: number) => formatUsd(v)} tick={{ fontSize: 12 }} width={80} />
            <Tooltip formatter={(v: number) => formatUsd(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {withSpend.map((v, i) => (
              <Bar
                key={v.vendor}
                dataKey={v.vendor}
                stackId="spend"
                fill={VENDOR_COLORS[i % VENDOR_COLORS.length]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div style={{ ...panel, overflowX: "auto" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: 12,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 16 }}>By vendor</h3>
          <span style={{ fontSize: 13, color: "#6b6259" }}>
            Click a vendor to see its line items for the highlighted month.
          </span>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr>
              <th style={th}>Vendor</th>
              {data.months.map((m) => (
                <th
                  key={m}
                  style={{
                    ...th,
                    textAlign: "right",
                    cursor: "pointer",
                    color: m === detail ? ACCENT : th.color,
                    textDecoration: m === detail ? "underline" : "none",
                  }}
                  onClick={() => setDetailMonth(m)}
                  title="Show line items for this month"
                >
                  {monthLabel(m)}
                  {m === thisMonth ? " (so far)" : ""}
                </th>
              ))}
              <th style={{ ...th, textAlign: "right" }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {vendors.map((v) => {
              const key = `${v.vendor}-${v.entry_id ?? "api"}`;
              const items = Object.entries(v.items[detail] ?? {}).sort((a, b) => b[1] - a[1]);
              return (
                <React.Fragment key={key}>
                  <tr
                    style={{ cursor: "pointer" }}
                    onClick={() => setExpanded(expanded === key ? null : key)}
                    aria-expanded={expanded === key}
                  >
                    <td style={td}>
                      <div style={{ fontWeight: 500 }}>
                        {expanded === key ? "▾" : "▸"} {v.vendor}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#6b6259",
                          display: "flex",
                          gap: 6,
                          alignItems: "center",
                          marginTop: 2,
                        }}
                      >
                        {v.category} <SourceBadge vendor={v} />
                      </div>
                    </td>
                    {data.months.map((m) => (
                      <td
                        key={m}
                        style={{ ...num, background: m === detail ? "#f8f7ff" : undefined }}
                      >
                        {v.status === "ok" ? formatUsd(v.monthly[m] ?? 0) : "—"}
                      </td>
                    ))}
                    <td style={{ ...num, fontWeight: 600 }}>
                      {v.status === "ok" ? formatUsd(sum(Object.values(v.monthly))) : "—"}
                    </td>
                  </tr>
                  {expanded === key && (
                    <tr>
                      <td colSpan={data.months.length + 2} style={{ ...td, background: "#fafafa" }}>
                        {v.status !== "ok" ? (
                          <span style={{ color: "#92400e" }}>
                            {v.status === "needs_access" ? `Needs ${v.needs.join(", ")}` : v.error}
                          </span>
                        ) : items.length === 0 ? (
                          <span style={{ color: "#6b6259" }}>
                            No charges in {monthLabel(detail)}.
                          </span>
                        ) : (
                          <table
                            style={{
                              width: "100%",
                              maxWidth: 640,
                              borderCollapse: "collapse",
                              fontSize: 13,
                            }}
                          >
                            <tbody>
                              {items.slice(0, 25).map(([name, amount]) => (
                                <tr key={name}>
                                  <td style={{ padding: "2px 8px" }}>{name}</td>
                                  <td style={{ padding: "2px 8px", textAlign: "right" }}>
                                    {formatUsd(amount)}
                                  </td>
                                </tr>
                              ))}
                              {items.length > 25 && (
                                <tr>
                                  <td style={{ padding: "2px 8px", color: "#6b6259" }}>
                                    {items.length - 25} smaller item
                                    {items.length - 25 === 1 ? "" : "s"}
                                  </td>
                                  <td style={{ padding: "2px 8px", textAlign: "right" }}>
                                    {formatUsd(sum(items.slice(25).map(([, a]) => a)))}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            <tr>
              <td style={{ ...td, fontWeight: 600 }}>Total</td>
              {data.months.map((m) => (
                <td key={m} style={{ ...num, fontWeight: 600 }}>
                  {formatUsd(totalFor(m))}
                </td>
              ))}
              <td style={{ ...num, fontWeight: 700 }}>
                {formatUsd(sum(data.months.map(totalFor)))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ ...panel, overflowX: "auto" }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 16 }}>
          Claude Code by person, {monthLabel(detail)}
        </h3>
        <p style={{ margin: "0 0 12px", color: "#6b6259", fontSize: 13 }}>
          Anthropic's per-person estimate for Claude Code used with our Console org. It is already
          inside the Anthropic total above, so it isn't added again. Claude Team, Max or Enterprise
          seats are billed separately; enter those by hand.
        </p>
        {data.claude_code.status !== "ok" ? (
          <div style={{ color: "#92400e", fontSize: 14 }}>
            {data.claude_code.status === "needs_access"
              ? `Needs ${data.claude_code.needs.join(", ")}.`
              : data.claude_code.error}
          </div>
        ) : people.length === 0 ? (
          <div style={{ color: "#6b6259", fontSize: 14 }}>
            No Claude Code usage in {monthLabel(detail)}.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr>
                <th style={th}>Person</th>
                <th style={{ ...th, textAlign: "right" }}>Estimated cost</th>
                <th style={{ ...th, textAlign: "right" }}>Sessions</th>
                <th style={{ ...th, textAlign: "right" }}>Lines added</th>
                <th style={{ ...th, textAlign: "right" }}>Commits</th>
                <th style={{ ...th, textAlign: "right" }}>PRs</th>
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <tr key={p.person}>
                  <td style={td}>{p.person}</td>
                  <td style={num}>{formatUsd(p.cost_usd)}</td>
                  <td style={num}>{formatInt(p.sessions)}</td>
                  <td style={num}>{formatInt(p.lines_added)}</td>
                  <td style={num}>{formatInt(p.commits)}</td>
                  <td style={num}>{formatInt(p.pull_requests)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ManualEntries data={data} onChanged={reload} />
    </div>
  );
};

export default OperatingCostsTab;
